import React, { useCallback, useEffect, useRef, useState } from 'react'
import { Box, Button, Theme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import useDeviceType from 'src/rsmCoreComponents/hooks/useDeviceType'
import { tokens } from 'src/styles/materialTheme'
import { Styles } from '../../types'
import { Interest } from './types'
import {
  ArrowDownIcon,
  ArrowUpIcon,
} from '../../rsmCoreComponents/components/icons'

const styles: Styles = {
  icon: (theme: Theme) => ({
    display: 'flex',
    flexDirection: 'column',
    justifyContent: 'center',
    alignItems: 'center',
    position: 'relative',
    padding: 0,
    width: '10rem',
    height: '4rem',
    textAlign: 'center',
    [theme.breakpoints.up('tablet')]: {
      height: '6.25rem',
      width: '6.875rem',
    },
    backgroundColor: tokens.colors.midnightBlue,
  }),
  emptyIcon: { height: '1.875rem' },
  moreLessInterests: { textAlign: 'end', width: '100%' },
  label: (theme: Theme) => ({
    '.MuiFormLabel-root': {
      fontSize: '1rem',
    },
    fontFamily: 'Prelo-Book,sans-serif',
    opacity: 1,
    fontSize: '1rem',
    color: theme.palette.text.primary,
    fontWeight: 700,
  }),
  button: (theme: Theme) => ({
    '.MuiFormLabel-root': {
      fontSize: '1rem',
    },
    fontFamily: 'Prelo-Book,sans-serif',
    opacity: 1,
    fontSize: '1rem',
    color: theme.palette.secondary.main,
    fontWeight: 500,
    alignItems: 'end',
    paddingRight: 0,
    '.MuiButton-endIcon': {
      display: 'inline',
      marginLeft: '0.25rem',
    },
    paddingY: 0,
    marginTop: '1rem',
    [theme.breakpoints.only('desktop')]: {
      marginTop: '0.5rem',
    },
  }),
  redesignName: { color: tokens.colors.white },
}

interface InterestsContainerProps {
  category: string
  interests: Interest[]
}

const InterestsSection = ({ category, interests }: InterestsContainerProps) => {
  const { t } = useTranslation()
  const { isMobile } = useDeviceType()
  const [showMore, setShowMore] = useState(false)
  const moreButtonRef = useRef<HTMLButtonElement>(null)
  const lessButtonRef = useRef<HTMLButtonElement>(null)
  const displayedInterests = interests?.slice(
    0,
    showMore ? interests.length : 4,
  )
  const firstNewItemRef = useRef<HTMLDivElement>(null)
  const handleShowMore = () => {
    setShowMore(true)
    setTimeout(() => {
      firstNewItemRef.current?.focus()
    }, 0)
  }

  const handleShowLess = () => {
    setShowMore(false)
  }

  const computedStyles = {
    unorderedList: useCallback(
      (theme: Theme) => ({
        display: 'flex',
        flexWrap: 'wrap',
        gap: '0.625rem',
        alignItems: 'flex-start',
        overflow: 'auto',
        margin: '0.25rem 0 -0.375rem',
        [theme.breakpoints.only('mobile')]: {
          gap: '1.25rem',
        },
        paddingBottom:
          interests?.length > 4 || interests?.length === 0 ? 0 : '1.5rem',
      }),
      [interests],
    ),
  }

  useEffect(() => {
    if (interests?.length === 0) {
      setShowMore(false)
    }
  }, [interests])

  useEffect(() => {
    if (showMore) {
      lessButtonRef.current?.focus()
    } else {
      moreButtonRef.current?.focus()
    }
  }, [showMore])

  return (
    <Box
      component="section"
      role="region"
      aria-labelledby={`${category}_Heading`}>
      <Box id={`${category}_Heading`} component="label" sx={styles.label}>
        {t(`UserProfile.InterestsSection.${category}`)}
      </Box>
      <Box
        id={`${category}_Interests`}
        aria-labelledby={`${category}_Button`}
        component="ul"
        sx={computedStyles.unorderedList}
        data-testid={`Div_ProfileInterestsCategory_${category}`}>
        {displayedInterests?.length === 0 && (
          <Box component="li" sx={styles.emptyIcon} />
        )}
        {displayedInterests?.map(
          (interest, index) =>
            interest.icon !== undefined && (
              <Box key={interest.name} component="li" sx={styles.container}>
                <Box
                  ref={showMore && index === 4 ? firstNewItemRef : null}
                  role="img"
                  aria-label={interest.name}
                  tabIndex={0}
                  sx={styles.icon}
                  data-testid={`Icon_${interest.name}_${index}`}>
                  {isMobile ? (
                    <Box sx={styles.redesignName}>{interest.name}</Box>
                  ) : (
                    interest.icon
                  )}
                </Box>
              </Box>
            ),
        )}
      </Box>
      <Box sx={styles.moreLessInterests}>
        {interests?.length > 4 && (
          <Button
            id={`${category}_Button`}
            aria-expanded={showMore}
            sx={styles.button}
            onClick={!showMore ? handleShowMore : handleShowLess}
            endIcon={
              !showMore ? (
                <ArrowDownIcon
                  sx={(theme: Theme) => ({
                    path: {
                      fill: theme.palette.secondary.main,
                    },
                  })}
                />
              ) : (
                <ArrowUpIcon
                  sx={(theme: Theme) => ({
                    rect: {
                      fill: theme.palette.secondary.main,
                    },
                  })}
                />
              )
            }>
            {!showMore
              ? t('UserProfile.InterestsSection.More').toLowerCase()
              : t('UserProfile.InterestsSection.Less').toLowerCase()}
          </Button>
        )}
      </Box>
    </Box>
  )
}

export default InterestsSection
